import React from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import { Database, Shield, Info, AlertTriangle } from 'lucide-react';

const DATA_TYPES = [
  { id: 'names', label: 'Names' },
  { id: 'contact', label: 'Contact details (email, phone, address)' },
  { id: 'demographic', label: 'Demographic information' },
  { id: 'health', label: 'Health / Medical information' },
  { id: 'financial', label: 'Financial information' },
  { id: 'opinions', label: 'Opinions / Attitudes' },
  { id: 'behavior', label: 'Behavioral data' },
  { id: 'location', label: 'Location data' },
  { id: 'biometric', label: 'Biometric data' },
  { id: 'genetic', label: 'Genetic data' },
  { id: 'criminal', label: 'Criminal records' },
  { id: 'employment', label: 'Employment information' },
  { id: 'education', label: 'Educational records' },
  { id: 'images', label: 'Images / Photos' },
  { id: 'audio', label: 'Audio recordings' },
  { id: 'video', label: 'Video recordings' },
  { id: 'other', label: 'Other personal data' }
];

const STORAGE_LOCATIONS = [
  { id: 'institutional', label: 'Institutional secure server' },
  { id: 'encrypted_drive', label: 'Encrypted external drive' },
  { id: 'cloud_institutional', label: 'Institutional cloud storage (OneDrive, Google Drive)' },
  { id: 'cloud_commercial', label: 'Commercial cloud storage' },
  { id: 'local_computer', label: 'Password-protected local computer' },
  { id: 'locked_cabinet', label: 'Locked filing cabinet (physical data)' },
  { id: 'research_data_management', label: 'Research data management system' },
  { id: 'other', label: 'Other' }
];

function D7_DataManagement() {
  const { dataE, updateDataRaw, nextStep, previousStep } = useWizard();
  const dataManagement = dataE.dataManagement;

  const updateDataManagement = (field, value) => {
    updateDataRaw({
      'wizardE.dataManagement': { ...dataManagement, [field]: value }
    });
  };

  const toggleDataType = (typeId) => {
    const currentTypes = dataManagement.dataTypes || [];
    const newTypes = currentTypes.includes(typeId)
      ? currentTypes.filter(t => t !== typeId)
      : [...currentTypes, typeId];
    updateDataManagement('dataTypes', newTypes);
  };

  const toggleStorageLocation = (locationId) => {
    const currentLocations = dataManagement.storageLocations || [];
    const newLocations = currentLocations.includes(locationId)
      ? currentLocations.filter(l => l !== locationId)
      : [...currentLocations, locationId];
    updateDataManagement('storageLocations', newLocations);
  };

  const hasSensitiveData = ['health', 'financial', 'biometric', 'genetic', 'criminal']
    .some(type => dataManagement.dataTypes?.includes(type));

  const isComplete = dataManagement.dataTypes.length > 0 &&
                     dataManagement.anonymizationMethod &&
                     dataManagement.storageLocations.length > 0 &&
                     dataManagement.retentionPeriod &&
                     dataManagement.gdprCompliant !== null;

  return (
    <div className="wizard-step">
      <div className="step-header">
        <Database className="step-icon" size={32} />
        <div>
          <h2>Step 7: Data Management</h2>
          <p className="step-description">
            Describe how you will store, protect, and manage research data throughout the project.
          </p>
        </div>
      </div>

      <div className="form-section">
        <h3>Types of Personal Data</h3>

        <div className="form-group">
          <label>What types of personal data will you collect? *</label>
          <div className="checkbox-grid">
            {DATA_TYPES.map(type => (
              <label key={type.id} className="checkbox-option">
                <input
                  type="checkbox"
                  checked={dataManagement.dataTypes?.includes(type.id) || false}
                  onChange={() => toggleDataType(type.id)}
                />
                <span>{type.label}</span>
              </label>
            ))}
          </div>
        </div>

        {hasSensitiveData && (
          <div className="info-box warning">
            <AlertTriangle size={20} />
            <div>
              <strong>Special Category Data</strong>
              <p>
                You have indicated you will collect sensitive data (health, financial, biometric, genetic, or criminal).
                This requires additional safeguards and justification under GDPR.
              </p>
            </div>
          </div>
        )}
      </div>

      <div className="form-section">
        <h3>Data Recording and Anonymization</h3>

        <div className="form-group">
          <label>How will data be recorded?</label>
          <textarea
            value={dataManagement.recordingMethod}
            onChange={(e) => updateDataManagement('recordingMethod', e.target.value)}
            placeholder="Describe how data will be recorded (e.g., paper forms, electronic surveys, audio recordings)"
            rows={3}
          />
        </div>

        <div className="form-group">
          <label>How will data be anonymized or pseudonymized? *</label>
          <textarea
            value={dataManagement.anonymizationMethod}
            onChange={(e) => updateDataManagement('anonymizationMethod', e.target.value)}
            placeholder="Describe how you will remove or replace identifying information (e.g., participant codes, removing names, aggregating data)"
            rows={4}
          />
          <small className="field-help">
            Anonymization: Completely removes identifying information. Pseudonymization: Replaces identifiers with codes while retaining a key.
          </small>
        </div>
      </div>

      <div className="form-section">
        <h3>Data Storage and Security</h3>

        <div className="form-group">
          <label>Where will data be stored? *</label>
          <div className="checkbox-grid">
            {STORAGE_LOCATIONS.map(location => (
              <label key={location.id} className="checkbox-option">
                <input
                  type="checkbox"
                  checked={dataManagement.storageLocations?.includes(location.id) || false}
                  onChange={() => toggleStorageLocation(location.id)}
                />
                <span>{location.label}</span>
              </label>
            ))}
          </div>
        </div>

        {dataManagement.storageLocations?.includes('other') && (
          <div className="form-group">
            <label>Please specify other storage location:</label>
            <input
              type="text"
              value={dataManagement.otherStorage}
              onChange={(e) => updateDataManagement('otherStorage', e.target.value)}
              placeholder="Describe the other storage location"
            />
          </div>
        )}

        <div className="form-group">
          <label>Who will have access to the data?</label>
          <textarea
            value={dataManagement.accessControl}
            onChange={(e) => updateDataManagement('accessControl', e.target.value)}
            placeholder="List who will have access to identifiable data and why they need access"
            rows={3}
          />
        </div>

        <div className="form-group">
          <label>Security Measures</label>
          <textarea
            value={dataManagement.securityMeasures}
            onChange={(e) => updateDataManagement('securityMeasures', e.target.value)}
            placeholder="Describe security measures (e.g., encryption, password protection, locked storage)"
            rows={3}
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Data Retention and Disposal</h3>

        <div className="form-group">
          <label>How long will data be retained? *</label>
          <select
            value={dataManagement.retentionPeriod}
            onChange={(e) => updateDataManagement('retentionPeriod', e.target.value)}
          >
            <option value="">Select retention period...</option>
            <option value="during_study">During study only</option>
            <option value="1_year">1 year after study completion</option>
            <option value="3_years">3 years after study completion</option>
            <option value="5_years">5 years after study completion</option>
            <option value="10_years">10 years after study completion</option>
            <option value="indefinite">Indefinitely (for future research)</option>
            <option value="other">Other (specify below)</option>
          </select>
        </div>

        <div className="form-group">
          <label>How will data be disposed of?</label>
          <textarea
            value={dataManagement.disposalMethod}
            onChange={(e) => updateDataManagement('disposalMethod', e.target.value)}
            placeholder="Describe how data will be securely disposed of when no longer needed (e.g., secure deletion, shredding)"
            rows={3}
          />
        </div>
      </div>

      <div className="form-section">
        <h3>Legal Compliance</h3>

        <div className="eligibility-question">
          <label className="question-label">
            <Shield size={18} style={{ display: 'inline', marginRight: '0.5rem' }} />
            Is your research GDPR compliant? *
          </label>
          <p className="question-help">
            GDPR (General Data Protection Regulation) applies to processing personal data of individuals in the EU/UK.
          </p>
          <div className="radio-group horizontal">
            <label className="radio-option">
              <input
                type="radio"
                name="gdprCompliant"
                checked={dataManagement.gdprCompliant === true}
                onChange={() => updateDataManagement('gdprCompliant', true)}
              />
              <span className="radio-label">Yes</span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="gdprCompliant"
                checked={dataManagement.gdprCompliant === false}
                onChange={() => updateDataManagement('gdprCompliant', false)}
              />
              <span className="radio-label">No / Not Applicable</span>
            </label>
          </div>
        </div>

        <div className="eligibility-question">
          <label className="question-label">
            Will a privacy notice be provided to participants?
          </label>
          <div className="radio-group horizontal">
            <label className="radio-option">
              <input
                type="radio"
                name="privacyNotice"
                checked={dataManagement.privacyNoticeIncluded === true}
                onChange={() => updateDataManagement('privacyNoticeIncluded', true)}
              />
              <span className="radio-label">Yes</span>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="privacyNotice"
                checked={dataManagement.privacyNoticeIncluded === false}
                onChange={() => updateDataManagement('privacyNoticeIncluded', false)}
              />
              <span className="radio-label">No</span>
            </label>
          </div>
        </div>
      </div>

      <NavigationButtons
        onPrevious={previousStep}
        onNext={isComplete ? nextStep : null}
        disableNext={!isComplete}
        nextLabel="Continue to Risk Assessment"
      />
    </div>
  );
}

export default D7_DataManagement;
